// Pages/OpsLegExecution.jsx
//
// Internal test tool — NOT part of the customer-facing flow.
// Requires an admin account to execute a leg: the provider-execution
// endpoints add an admin-only dependency on top of the router-level guard.
//
// Lets staff paste a transfer ID, see its legs, and trigger the real
// provider-execution call (POST /provider-execution/legs/{id}/execute) so we
// can confirm a single call is actually reaching Bridge/Zuba/Obiex.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, PlayCircle, Loader2, RefreshCcw } from "lucide-react";
import { transfersService } from "../services/transfers.service";
import { executionService } from "../services/execution.service";

export function OpsLegExecution() {
  const navigate = useNavigate();
  const [transferId, setTransferId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [executingLegId, setExecutingLegId] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  async function loadTransfer() {
    if (!transferId.trim()) return;
    setError(null);
    setLastResult(null);
    setLoading(true);
    try {
      const data = await transfersService.getTransferDetail(transferId.trim());
      setDetail(data);
    } catch {
      setError("Couldn't load that transfer. Check the ID and your role.");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleExecute(legId) {
    setError(null);
    setExecutingLegId(legId);
    setLastResult(null);
    try {
      const result = await executionService.executeLeg(legId);
      setLastResult({ legId, result });
      await loadTransfer();
    } catch {
      setLastResult({
        legId,
        error: "Execution call failed. Check engineering logs for details.",
      });
    } finally {
      setExecutingLegId(null);
    }
  }

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-24 text-left">

      {/* HEADER - mobile only */}
      <div className="md:hidden bg-[#0A4730] rounded-b-[50px] px-6 pt-12 pb-8">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:scale-95 transition"
          >
            <ChevronLeft className="text-white" size={20} />
          </button>
          <div>
            <p className="text-[12px] text-white opacity-80 mb-1">Internal tool</p>
            <h1 className="text-2xl text-[#C0F6B7] font-serif font-semibold leading-tight">
              Leg Execution
            </h1>
          </div>
        </div>
      </div>

      {/* DESKTOP HEADER */}
      <div className="hidden md:flex items-center gap-3 max-w-2xl mx-auto px-5 pt-10 pb-2">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full border border-slate-200 bg-white flex items-center justify-center active:scale-95 transition text-[#0A4730]/60 hover:border-[#0A4730]/30"
        >
          <ChevronLeft size={18} />
        </button>
        <h1 className="text-2xl text-[#0A4730] font-serif font-semibold">
          Leg Execution
        </h1>
      </div>

      {/* CONTENT */}
      <div className="max-w-2xl mx-auto px-5 py-8 space-y-6">

        <div className="rounded-2xl bg-amber-50 border border-amber-100 p-4 text-amber-700 text-sm">
          Internal test tool. Requires an <strong>admin</strong> role — the
          backend rejects this call from any other account.
        </div>

        <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] p-5 space-y-4">
          <label className="block text-sm font-semibold text-[#0A2540]">
            Transfer ID
          </label>
          <div className="flex gap-2">
            <input
              value={transferId}
              onChange={(e) => setTransferId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              className="flex-1 h-12 rounded-xl border border-slate-200 px-4 text-sm text-[#0A2540] focus:outline-none focus:border-[#0A4730]/40"
            />
            <button
              onClick={loadTransfer}
              disabled={loading}
              className="h-12 px-5 rounded-xl bg-[#0A4730] text-white font-semibold text-sm hover:bg-[#0A4730]/90 transition disabled:opacity-60 flex items-center gap-2"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCcw size={16} />}
              Load
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-red-500 text-sm">
            {error}
          </div>
        )}

        {detail && (
          <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] p-5 space-y-3">
            <p className="text-sm font-semibold text-[#0A2540] mb-2">
              Legs ({detail.legs?.length || 0})
            </p>
            {(detail.legs || []).map((leg) => (
              <div
                key={leg.id}
                className="rounded-2xl border border-slate-100 p-4 flex items-center justify-between gap-4"
              >
                <div>
                  <p className="text-sm font-semibold text-[#0A2540]">
                    {leg.leg_type} · {leg.provider || "—"}
                  </p>
                  <p className="text-xs text-[#0A2540]/50">
                    {leg.source_currency} → {leg.destination_currency} · status:{" "}
                    <span className="font-semibold">{leg.status}</span>
                  </p>
                  {leg.provider_reference && (
                    <p className="text-xs text-[#0A2540]/40 mt-1">
                      ref: {leg.provider_reference}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleExecute(leg.id)}
                  disabled={executingLegId === leg.id || leg.status !== "pending"}
                  className="shrink-0 h-10 px-4 rounded-xl bg-[#0A4730] text-white text-xs font-bold hover:bg-[#0A4730]/90 transition disabled:opacity-40 flex items-center gap-2"
                >
                  {executingLegId === leg.id ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <PlayCircle size={14} />
                  )}
                  Execute
                </button>
              </div>
            ))}
          </div>
        )}

        {lastResult && (
          <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] p-5">
            <p className="text-sm font-semibold text-[#0A2540] mb-2">
              Result for leg {lastResult.legId}
            </p>
            <pre className="text-xs bg-slate-50 rounded-xl p-4 overflow-x-auto text-[#0A2540]/70">
              {JSON.stringify(lastResult.result ?? lastResult.error, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
